<script setup lang="ts">
import ApiTokenIssueDialog from '@/components/dialogs/ApiTokenIssueDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const { t } = useI18n()
const page = 'api-tokens-page'

interface ApiToken {
  id: number
  name: string
  abilities: string[]
  last_used_at: string | null
  expires_at: string | null
  created_at: string | null
}

interface UserOption {
  id: string
  email: string
  profile: {
    name: string
    last_name: string
  }
}

// User autocomplete
const userSearch = ref('')
const debouncedUserSearch = useDebounce(userSearch, 300)
const selectedUserId = ref<string | null>(null)

const usersUrl = createUrl('/users/', {
  query: {
    search: debouncedUserSearch,
    page: 1,
    itemsPerPage: 20,
    sortBy: 'id',
    orderBy: 'desc',
  },
})

const { data: usersData } = await useApi<any>(usersUrl)

const userOptions = computed(() =>
  (usersData.value?.data ?? []).map((user: UserOption) => ({
    title: `${user.profile.name} ${user.profile.last_name} (${user.email})`,
    value: user.id,
  })),
)

// Tokens table
const tokens = ref<ApiToken[]>([])
const isLoadingTokens = ref(false)

const headers = [
  { title: t(`${page}.token_name`), key: 'name' },
  { title: t(`${page}.abilities`), key: 'abilities', sortable: false },
  { title: t(`${page}.created_at`), key: 'created_at' },
  { title: t(`${page}.last_used_at`), key: 'last_used_at' },
  { title: t(`${page}.expires_at`), key: 'expires_at' },
  { title: t(`${page}.actions`), key: 'actions', sortable: false },
]

const fetchTokens = async () => {
  if (!selectedUserId.value) {
    tokens.value = []

    return
  }

  isLoadingTokens.value = true
  try {
    tokens.value = await $api(`/admin/users/${selectedUserId.value}/api-tokens`) as ApiToken[]
  }
  catch (e) {
    console.error('Failed to load tokens', e)
    tokens.value = []
  }
  finally {
    isLoadingTokens.value = false
  }
}

watch(selectedUserId, fetchTokens)

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : '-'

// Issue dialog
const isIssueDialogOpen = ref(false)

// Revoke
const isRevokeDialogOpen = ref(false)
const revokeTokenId = ref<number | null>(null)

const openRevokeDialog = (tokenId: number) => {
  revokeTokenId.value = tokenId
  isRevokeDialogOpen.value = true
}

const handleRevoke = async (confirmed: boolean) => {
  if (!confirmed || revokeTokenId.value === null)
    return

  try {
    await $api(`/admin/api-tokens/${revokeTokenId.value}`, { method: 'DELETE' })
    await fetchTokens()
  }
  catch (e) {
    console.error('Failed to revoke token', e)
  }
}
</script>

<template>
  <VCard>
    <VCardText class="d-flex flex-wrap align-center gap-4">
      <VAutocomplete
        v-model="selectedUserId"
        v-model:search="userSearch"
        :items="userOptions"
        :label="t(`${page}.select_user`)"
        :no-data-text="t(`${page}.no_users_found`)"
        clearable
        style="max-inline-size: 420px;"
      />
      <VSpacer />
      <VBtn
        :disabled="!selectedUserId"
        @click="isIssueDialogOpen = true"
      >
        {{ t(`${page}.issue_token`) }}
      </VBtn>
    </VCardText>

    <VDataTable
      v-if="selectedUserId"
      :items="tokens"
      :headers="headers"
      :loading="isLoadingTokens"
      :no-data-text="t(`${page}.no_tokens`)"
      item-value="id"
    >
      <template #item.abilities="{ item }">
        <VChip
          v-for="ability in item.abilities"
          :key="ability"
          size="small"
          class="me-1"
        >
          {{ ability }}
        </VChip>
      </template>

      <template #item.created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <template #item.last_used_at="{ item }">
        {{ formatDate(item.last_used_at) }}
      </template>

      <template #item.expires_at="{ item }">
        {{ formatDate(item.expires_at) }}
      </template>

      <template #item.actions="{ item }">
        <VBtn
          size="small"
          color="error"
          variant="text"
          @click="openRevokeDialog(item.id)"
        >
          {{ t(`${page}.revoke`) }}
        </VBtn>
      </template>

      <template #bottom />
    </VDataTable>

    <VCardText
      v-else
      class="text-medium-emphasis"
    >
      {{ t(`${page}.select_user_hint`) }}
    </VCardText>
  </VCard>

  <ApiTokenIssueDialog
    v-model:isDialogVisible="isIssueDialogOpen"
    :user-id="selectedUserId"
    @issued="fetchTokens"
  />

  <ConfirmDialog
    v-model:isDialogVisible="isRevokeDialogOpen"
    :confirmation-question="t(`${page}.revoke_confirm`)"
    :confirm-title="t(`${page}.revoke`)"
    confirm-msg=""
    :cancel-title="t('cancel')"
    cancel-msg=""
    @confirm="handleRevoke"
  />
</template>
